import React from 'react';

import { dashboardPdfApis, pdfApis } from '@apis/next/pdfApis';
import { dashboardSeaWaybillApis, seaWaybillApis } from '@apis/next/seaWaybillApis';
import { Layout } from '@components/Layout';
import { seaWaybillStrings } from '@data/seaWaybill';
import { useSearch } from '@hooks/next/useSearch';
import { useBlobURL } from '@hooks/useBlobURL';
import { useIsAdmin } from '@hooks/useIsAdmin';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { printBlob } from '@utils/printBlob';
import { toJST } from '@utils/types';
import { Document, Page } from 'react-pdf';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    print: '印刷',
    history: '変更履歴',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  exportSeaWaybillMainContainer: {
    margin: '0 auto',
    paddingTop: '4rem',
  },
  footer: {
    ...common.flexInline,
    ...common.justifyBetween,
    ...common.middleText,
    marginTop: '2rem',
  },
}));

export const ExportSeaWaybill: React.FC = () => {
  const isAdmin = useIsAdmin();

  const { search } = useSearch(
    z.object({
      id: z.number(),
      d2dNo: z.string(),
    }),
  );

  const seaWaybillQuery = isAdmin
    ? dashboardSeaWaybillApis.useGet('/dashboard/sea-waybill/:id', {
        params: {
          id: search.id,
        },
      })
    : seaWaybillApis.useGet('/sea-waybill/:id', {
        params: {
          id: search.id,
        },
      });

  const pdfQuery = isAdmin
    ? dashboardPdfApis.useGet(
        '/dashboard/pdf/SeaWaybill/:d2d_no',
        {
          params: {
            d2d_no: search.d2dNo,
          },
        },
        {
          staleTime: Infinity,
        },
      )
    : pdfApis.useGet(
        '/pdf/SeaWaybill/:d2d_no',
        {
          params: {
            d2d_no: search.d2dNo,
          },
        },
        {
          staleTime: Infinity,
        },
      );

  const pdfUrl = useBlobURL(pdfQuery.data);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const pdfWidth = 1152;

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      title={`${seaWaybillStrings.seaWaybill} - ${search.d2dNo}`}
      loading={pdfQuery.isLoading || seaWaybillQuery.isLoading}
      errors={[pdfQuery.error || seaWaybillQuery.error]}
      classes={{
        background: classes.background,
      }}
    >
      <div className={classes.exportSeaWaybillMainContainer}>
        <div style={{ width: pdfWidth }}>
          <Document file={pdfUrl} loading="">
            <Page pageNumber={1} width={pdfWidth} />
          </Document>
        </div>

        <div className={classes.footer}>
          <div>
            Last update:{' '}
            <time>
              {seaWaybillQuery.data === undefined
                ? '-'
                : toJST(new Date(seaWaybillQuery.data!.updated_at))}
            </time>
          </div>
          <div style={{ display: 'flex', columnGap: '1rem' }}>
            <a className={classes.link} onClick={() => printBlob(pdfUrl!)}>
              {strings.print}
            </a>

            <a
              className={classes.link}
              href={`${
                isAdmin
                  ? url('/admin/booking/PrintSeaWaybillLog')
                  : url('/utilities/Bookings/PrintSeaWaybillLog')
              }?id=${search.id}`}
              target="_blank"
            >
              {strings.history}
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};
